import { isDevelopment, isProduction } from 'helpers/utils/environment';
import { logMessage, logError } from './logging';

/**
 * Fetches data from the given URL with error handling.
 */
export async function fetchData(
  url: string,
  headers: Record<string, string>,
  method = 'POST',
  body: any = null,
  retries = 1
): Promise<any> {
  try {
    const response = await fetch(url, {
      method,
      headers,
      body: body ? JSON.stringify(body) : null,
    });
    
    if (!response?.ok) {
      const errorText = await response.text().catch(() => 'No error text available');
      const error = new Error(`HTTP error: ${response.status} - ${errorText}`);
      logError(`HTTP error::status: ${response.status}`, error);
      
      if (retries > 0) {
        logMessage(`Retrying request to ${url} (${retries} attempts left)`);
        return fetchData(url, headers, method, body, retries - 1);
      }
      
      // In development, throw the error for better debugging
      if (isDevelopment()) {
        throw error;
      }
      
      // In production, return an error object instead of throwing
      return {
        isError: true,
        error: true,
        message: `Failed to fetch data from ${url}`,
        status: response?.status || 500,
        data: null
      };
    }
    
    return await response.json();
  } catch (error) {
    logError(`Fetch Error:: Could not fetch data from ${url}`, error);
    
    if (retries > 0) {
      logMessage(`Retrying request to ${url} (${retries} attempts left)`);
      return fetchData(url, headers, method, body, retries - 1);
    }
    
    // In development, re-throw the error for better debugging
    if (isDevelopment() && !isProduction()) {
      throw error;
    }
    
    // In production, return an error object instead of throwing
    return {
      isError: true,
      error: true,
      message: `Failed to fetch data from ${url}`,
      status: null,
      data: null
    };
  }
}